import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Alert, CircularProgress,
  TextField, Button, Chip, Divider, Card, CardContent, CardActions,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
} from '@mui/material';
import {
  TvOutlined as DisplayIcon, Palette as PaletteIcon,
  OpenInNew as OpenIcon, QrCode as QrCodeIcon,
} from '@mui/icons-material';
import wagService, { WAGTenantConfig, WAGAgencia } from '../../services/wag.service';

const WAGDisplay: React.FC = () => {
  const [config, setConfig] = useState<WAGTenantConfig | null>(null);
  const [agencias, setAgencias] = useState<WAGAgencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState<Partial<WAGTenantConfig>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, ags] = await Promise.all([wagService.getConfig(), wagService.getAgencias()]);
      setConfig(cfg);
      setAgencias(ags.filter(a => a.estado === 'A'));
      setForm({
        displayCorFundo: cfg.displayCorFundo || '#0f0f1a',
        displayCorTexto: cfg.displayCorTexto || '#ffffff',
        displayCorDestaque: cfg.displayCorDestaque || '#e94560',
        displayLogo: cfg.displayLogo || '',
      });
    } catch {
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await wagService.updateConfig(form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <DisplayIcon sx={{ color: '#e94560' }} />
        <Typography variant="h5" fontWeight={700}>Display / Painel TV</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Configure a aparência do painel de chamadas e gere os links para as televisões
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Configurações guardadas!</Alert>}

      <Grid container spacing={3}>
        {/* Configuração visual */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <PaletteIcon color="action" />
              <Typography variant="h6" fontWeight={600}>Aparência</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="URL do Logo"
                  fullWidth size="small"
                  value={form.displayLogo ?? ''}
                  onChange={e => setForm(p => ({ ...p, displayLogo: e.target.value }))}
                  helperText="URL pública da imagem (PNG/SVG recomendado)"
                />
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Cor de fundo</Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <input type="color" value={form.displayCorFundo ?? '#0f0f1a'}
                      onChange={e => setForm(p => ({ ...p, displayCorFundo: e.target.value }))}
                      style={{ width: 40, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
                    <TextField size="small" value={form.displayCorFundo ?? ''}
                      onChange={e => setForm(p => ({ ...p, displayCorFundo: e.target.value }))}
                      sx={{ flex: 1 }} inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Cor do texto</Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <input type="color" value={form.displayCorTexto ?? '#ffffff'}
                      onChange={e => setForm(p => ({ ...p, displayCorTexto: e.target.value }))}
                      style={{ width: 40, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
                    <TextField size="small" value={form.displayCorTexto ?? ''}
                      onChange={e => setForm(p => ({ ...p, displayCorTexto: e.target.value }))}
                      sx={{ flex: 1 }} inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Cor de destaque (senhas)</Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <input type="color" value={form.displayCorDestaque ?? '#e94560'}
                      onChange={e => setForm(p => ({ ...p, displayCorDestaque: e.target.value }))}
                      style={{ width: 40, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
                    <TextField size="small" value={form.displayCorDestaque ?? ''}
                      onChange={e => setForm(p => ({ ...p, displayCorDestaque: e.target.value }))}
                      sx={{ flex: 1 }} inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }} />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Preview */}
            <Box mt={3} p={2} borderRadius={2} display="flex" flexDirection="column" alignItems="center" gap={1}
              sx={{ background: form.displayCorFundo, color: form.displayCorTexto, minHeight: 120 }}>
              {form.displayLogo && <img src={form.displayLogo} alt="logo" style={{ height: 32, objectFit: 'contain' }} />}
              <Typography variant="caption" sx={{ opacity: 0.6 }}>A ser atendido</Typography>
              <Typography variant="h4" fontWeight={900} sx={{ color: form.displayCorDestaque }}>A001</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>Pagamentos · Balcão 1</Typography>
            </Box>

            <Button fullWidth variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 3, bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
              {saving ? 'A guardar…' : 'Guardar aparência'}
            </Button>
          </Paper>
        </Grid>

        {/* Links por agência */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <DisplayIcon color="action" />
              <Typography variant="h6" fontWeight={600}>Links das Agências</Typography>
            </Box>
            {agencias.length === 0 ? (
              <Alert severity="info">Crie primeiro uma agência para obter os links do display.</Alert>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                {agencias.map(ag => (
                  <Card key={ag.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography fontWeight={600}>{ag.desig}</Typography>
                        <Chip label={ag.codigo} size="small" variant="outlined" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" mt={1} fontFamily="monospace" fontSize={12}
                        sx={{ wordBreak: 'break-all' }}>
                        Display: /display/{ag.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontFamily="monospace" fontSize={12}
                        sx={{ wordBreak: 'break-all' }}>
                        Quiosque: /kiosk/{ag.qrToken}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ pt: 0, gap: 1 }}>
                      <Button size="small" startIcon={<DisplayIcon />}
                        href={`https://wag.cv/display/${ag.id}`} target="_blank" rel="noopener">
                        Abrir TV
                      </Button>
                      <Button size="small" startIcon={<QrCodeIcon />}
                        href={`https://wag.cv/kiosk/${ag.qrToken}`} target="_blank" rel="noopener">
                        Quiosque
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WAGDisplay;
